import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { cn } from "@/lib/utils";
import {
  AlertTriangleIcon,
  FileUpIcon,
  LoaderCircleIcon,
  PaperclipIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Where the file stands, once the composer has had it checked.
 *
 * `blocked` is a refusal (the document cannot be sent at all), `warning` is an
 * opinion (it can, but it is probably a waste). Both are shown on the card
 * itself: the user has to know *which* of two attachments is the problem, and a
 * banner alone could only name it in prose.
 */
export type FileCardStatus = "checking" | "ok" | "blocked" | "warning";

type FileCardProps = {
  url: string;
  filename: string;
  className?: string;
  onRemove?: () => void;
  variant?: "card" | "pill";
  status?: FileCardStatus;
  /** One short line saying what is wrong, shown in the card's tooltip. */
  issueLabel?: string;
};

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg"];

export default function FileCard({
  url,
  filename,
  className,
  onRemove,
  variant = "card",
  status = "ok",
  issueLabel,
}: FileCardProps) {
  const { isMobile } = useDeviceFormat();
  const extension = filename.split(".").pop()?.toLowerCase() ?? "";
  const isImage = IMAGE_EXTENSIONS.includes(extension);
  // Inline PDF preview via <object> renders through a native plugin that
  // crashes mobile browsers / the PWA webview when fed a blob: URL. Restrict it
  // to desktop and fall back to the file icon everywhere else.
  const canPreviewPdf = extension === "pdf" && !isMobile;
  const [imageError, setImageError] = useState(false);
  const isChecking = status === "checking";
  const isBlocked = status === "blocked";
  const isWarning = status === "warning";

  useEffect(() => {
    setImageError(false);
  }, [url]);

  if (variant === "pill") {
    return (
      <div
        title={issueLabel}
        className={cn(
          "inline-flex items-center gap-1.5 max-w-[200px] rounded-full border bg-secondary py-1 pr-1 text-secondary-foreground",
          isImage && !imageError ? "pl-1" : "pl-2.5",
          isBlocked && "border-destructive bg-destructive/10",
          isWarning && "border-amber-500 bg-amber-500/10",
          className,
        )}
      >
        {isChecking ? (
          <LoaderCircleIcon className="size-3.5 shrink-0 animate-spin text-muted-foreground" />
        ) : isBlocked || isWarning ? (
          <AlertTriangleIcon
            className={cn(
              "size-3.5 shrink-0",
              isBlocked ? "text-destructive" : "text-amber-600 dark:text-amber-500",
            )}
          />
        ) : isImage && !imageError ? (
          <img
            src={url}
            alt={filename}
            className="size-6 shrink-0 rounded-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <PaperclipIcon className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate text-xs font-medium">{filename}</span>
        {onRemove && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }}
            className="flex shrink-0 items-center justify-center rounded-full size-5 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
            aria-label="Remove file"
          >
            <XIcon className="size-3.5" />
          </button>
        )}
      </div>
    );
  }

  const content = (
    <>
      {isChecking && (
        // Over the preview rather than replacing it: the card keeps its size and
        // its name, so the row does not jump when the verdict lands.
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/70">
          <LoaderCircleIcon className="size-4 animate-spin text-muted-foreground" />
        </div>
      )}
      {(isBlocked || isWarning) && (
        // Solid fill with a small icon inside, rather than a large icon on a
        // pale disc: at this size the triangle's own corners reach the edge of
        // the disc and read as clipped.
        <div
          className={cn(
            "absolute top-1 right-1 z-10 flex items-center justify-center rounded-full size-5 shadow-sm",
            isBlocked
              ? "bg-destructive text-destructive-foreground"
              : "bg-amber-500 text-white",
          )}
        >
          <AlertTriangleIcon className="size-3" aria-hidden="true" />
          {/* The icon is the same in both cases and only the fill colour
              differs, so on its own the badge says "blocked" and "already
              sent" identically to a screen reader and to anyone who does not
              separate red from amber. The tooltip carries the label, but only
              on hover and on a container nothing can focus. */}
          <span className="sr-only">{issueLabel}</span>
        </div>
      )}
      {onRemove && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          className="absolute -top-1.5 -right-1.5 z-10 flex items-center justify-center size-5 rounded-full border bg-secondary text-secondary-foreground opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity hover:bg-destructive hover:text-destructive-foreground"
          aria-label="Remove file"
        >
          <span className="text-xs leading-none">&times;</span>
        </button>
      )}
      <div className="relative">
        {isImage && !imageError ? (
          <img
            src={url}
            alt={filename}
            className="block object-cover w-full h-16 rounded-t-lg"
            onError={() => setImageError(true)}
          />
        ) : canPreviewPdf ? (
          <object
            data={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
            type="application/pdf"
            className="w-full h-16 rounded-t-lg pointer-events-none"
          >
            <div className="flex items-center justify-center w-full h-16 bg-muted">
              <FileUpIcon className="size-5 text-muted-foreground" />
            </div>
          </object>
        ) : (
          <div className="flex items-center justify-center w-full h-16 bg-muted rounded-t-lg">
            <FileUpIcon className="size-5 text-muted-foreground" />
          </div>
        )}
        <Badge
          variant="secondary"
          className="absolute bottom-1 left-1 px-1 py-0 text-[9px] leading-tight uppercase"
        >
          {extension || "FILE"}
        </Badge>
      </div>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div className="px-1.5 py-1">
            <span className="block text-[10px] font-medium truncate text-muted-foreground">
              {filename}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p>{filename}</p>
          {issueLabel && (
            <p
              className={cn(
                "mt-0.5",
                isBlocked ? "text-destructive" : "text-amber-600 dark:text-amber-500",
              )}
            >
              {issueLabel}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </>
  );

  if (onRemove) {
    return (
      <div
        className={cn(
          "group relative flex-shrink-0 w-20 rounded-lg border bg-muted",
          isBlocked && "border-destructive",
          isWarning && "border-amber-500",
          className,
        )}
      >
        {content}
      </div>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "flex-shrink-0 w-20 rounded-lg border bg-muted hover:opacity-80 transition-opacity",
        className,
      )}
    >
      {content}
    </a>
  );
}
